import { useMount } from "shared-utils";

import type { BankConnectSchema } from "../../api-types/bankconnect_schemas";
import { useBankConnectFactory } from "../../factories/use-bank-connect-factory";

export interface ReconnectBankAccountModalProps {
  bankConnectProps: BankConnectSchema;
  /** Public id of the bank connection to reconnect. */
  connectionId: string;
  /**
   * Called once the connection is reconnected — the caller should refetch the connections list
   * and close the flow. This fires for inline Plaid reconnects; Plaid OAuth and Basiq reconnects
   * redirect away and complete through the add-accounts host's resume/account-selection path on
   * return, so they invalidate there instead.
   */
  onReconnected: () => void;
  /** Called when the reconnect is abandoned or errors, so the caller can unmount this host. */
  onClose: () => void;
}

/**
 * Launches a bank connection's provider reconnect flow (Plaid update mode / Basiq reconnection)
 * inline from surfaces like the profile connections page. Renders nothing — the provider takes
 * over the UI. Render it only while a reconnect is active; the connect factory initialises
 * provider clients on mount.
 */
export const ReconnectBankAccountModal = ({
  bankConnectProps,
  connectionId,
  onReconnected,
  onClose,
}: ReconnectBankAccountModalProps) => {
  const { useBankConnect } = useBankConnectFactory(bankConnectProps, {
    onReconnectionCallback: onReconnected,
    // Plaid Link exit / connect error: close so the host unmounts instead of dead-ending.
    onErrorOrAbandon: onClose,
  });
  const { startReconnect } = useBankConnect();

  // Read the connection from the authoritative route-data list (all providers) so startReconnect
  // routes to the right provider (Plaid update mode / Basiq reconnection).
  const connection = bankConnectProps.bankSettings.initialBankingData.bankingDataSources.find(
    (bankConnection) => bankConnection.id === connectionId
  );

  useMount(() => {
    if (connection) {
      startReconnect(connection);
    } else {
      onClose();
    }
  });

  return null;
};
